import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import authService from '../services/authService';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if user is authenticated on mount
  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');
      
      if (token && storedUser) {
        try {
          // First set user from localStorage immediately (preserving adminRole)
          const parsedUser = JSON.parse(storedUser);
          setUser(parsedUser);
          
          // Then validate token in background
          const response = await authService.validateToken();
          if (response.valid) {
            // IMPORTANT: Preserve adminRole from localStorage if server returns 'user' but localStorage has admin
            // This prevents race conditions during page refresh
            const serverUser = response.user;
            const finalUser = {
              ...serverUser,
              // Keep the adminRole from localStorage if it's admin/subadmin and server returns 'user' or undefined
              adminRole: (parsedUser.adminRole === 'admin' || parsedUser.adminRole === 'subadmin') 
                ? (serverUser.adminRole || parsedUser.adminRole)
                : (serverUser.adminRole || 'user')
            };
            setUser(finalUser);
            localStorage.setItem('user', JSON.stringify(finalUser));
          }
        } catch (err) {
          console.error('Token validation error:', err);
          // Don't remove token on validation error - might be network issue
          // Keep user logged in with localStorage data
          const userData = localStorage.getItem('user');
          if (userData) {
            setUser(JSON.parse(userData));
          }
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const login = async (email, password, rememberMe = false) => {
    try {
      setError(null);
      console.log('=== LOGIN ATTEMPT ===');
      console.log('Email:', email);
      const response = await authService.login(email, password, rememberMe);
      console.log('Login response:', response);
      
      if (response.token) {
        console.log('Login successful! Token received');
        localStorage.setItem('token', response.token);
        
        // Store user without large base64 images to avoid localStorage quota issues
        const userToStore = { ...response.user };
        if (userToStore.companyLogo && userToStore.companyLogo.length > 1000) {
          // Store only the reference, not the full base64 image
          userToStore.companyLogo = userToStore.companyLogo.substring(0, 100) + '...truncated';
          userToStore.hasCompanyLogo = true;
        }
        if (userToStore.profileImage && userToStore.profileImage.length > 1000) {
          userToStore.profileImage = userToStore.profileImage.substring(0, 100) + '...truncated';
          userToStore.hasProfileImage = true;
        }
        
        localStorage.setItem('user', JSON.stringify(userToStore));
        setUser(response.user); // Keep full user in memory
        return { success: true, user: response.user };
      }
      
      console.log('Login failed: no token in response');
      return { success: false, error: response.error || 'Login failed' };
    } catch (err) {
      console.error('Login error:', err);
      const errorMessage = err.response?.data?.error || 'Login failed. Please try again.';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const register = async (email, password, confirmPassword) => {
    try {
      setError(null);
      const response = await authService.register(email, password, confirmPassword);
      
      if (response.token) {
        localStorage.setItem('token', response.token);
        localStorage.setItem('user', JSON.stringify(response.user));
        setUser(response.user);
        return { success: true, user: response.user };
      }
      
      return { success: false, error: response.error || 'Registration failed' };
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Registration failed. Please try again.';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const logout = useCallback(async () => {
    try {
      await authService.logout();
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setUser(null);
    }
  }, []);

  const updateProfile = async (profileData) => {
    try {
      setError(null);
      const response = await authService.updateProfile(profileData);
      
      if (response.user) {
        localStorage.setItem('user', JSON.stringify(response.user));
        setUser(response.user);
        return { success: true, user: response.user };
      }
      
      return { success: false, error: response.error || 'Profile update failed' };
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Profile update failed. Please try again.';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // Refresh user data from localStorage or API
  const refreshUser = (newUserData) => {
    if (newUserData) {
      // Update React state immediately (for UI)
      setUser(newUserData);
      
      // For localStorage, handle base64 images carefully to avoid quota issues
      try {
        const userForStorage = { ...newUserData };
        // If logo/avatar is base64, don't store it in localStorage (too large)
        // Just keep it in React state for the current session
        if (userForStorage.companyLogo && userForStorage.companyLogo.startsWith('data:')) {
          userForStorage.companyLogo = null; // Don't persist base64 in localStorage
        }
        if (userForStorage.avatar && userForStorage.avatar.startsWith('data:')) {
          userForStorage.avatar = null; // Don't persist base64 in localStorage
        }
        localStorage.setItem('user', JSON.stringify(userForStorage));
      } catch (e) {
        console.warn('Could not save user to localStorage:', e);
        // Still keep user in React state even if localStorage fails
      }
    } else {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        setUser(JSON.parse(storedUser));
      }
    }
  };

  const becomeSupplier = async () => {
    try {
      setError(null);
      const response = await authService.becomeSupplier();
      
      if (response.user) {
        localStorage.setItem('user', JSON.stringify(response.user));
        setUser(response.user);
        return { success: true, user: response.user };
      }
      
      return { success: false, error: response.error || 'Failed to become supplier' };
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Failed to become supplier. Please try again.';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const becomeBuyer = async () => {
    try {
      setError(null);
      const response = await authService.becomeBuyer();
      
      if (response.user) {
        localStorage.setItem('user', JSON.stringify(response.user));
        setUser(response.user);
        return { success: true, user: response.user };
      }
      
      return { success: false, error: response.error || 'Failed to become buyer' };
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Failed to become buyer. Please try again.';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const verifyEmail = async (email, code) => {
    try {
      setError(null);
      const response = await authService.verifyEmail(email, code);
      
      if (response.user) {
        localStorage.setItem('token', response.token);
        localStorage.setItem('user', JSON.stringify(response.user));
        setUser(response.user);
        return { success: true, user: response.user };
      }
      
      return { success: false, error: response.error || 'Email verification failed' };
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Email verification failed. Please try again.';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const clearError = () => {
    setError(null);
  };

  // Check if user has admin access
  const isAdmin = user?.adminRole === 'admin' || user?.adminRole === 'subadmin';

  const value = {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    isAdmin,
    isBuyer: user?.isBuyer || false,
    isSupplier: user?.isSupplier || false,
    login,
    register,
    logout,
    updateProfile,
    refreshUser,
    becomeSupplier,
    becomeBuyer,
    verifyEmail,
    clearError
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
